import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  CalendarIcon,
  ExternalLinkIcon,
  EyeIcon,
  UserIcon,
  WalletIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { performanceApi } from "../api/client";
import { PerformanceBrandPrintMenu } from "../ui/PerformanceBrandPrintMenu";
import { usePayoutsQuery } from "../api/queries";

const STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  paid: "outline",
  cancelled: "destructive",
} as const;

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

function formatDate(value: Date | string | null | undefined) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("lo-LA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function PayoutsPage() {
  const nav = useNavigate({ from: "/app/performance/payouts" });
  const payoutsQuery = usePayoutsQuery();
  const [status, setStatus] = useState("all");
  const payouts = payoutsQuery.data ?? [];
  const filtered = useMemo(
    () =>
      status === "all"
        ? payouts
        : payouts.filter((payout) => payout.status === status),
    [payouts, status],
  );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            { label: "ໃບເບີກຈ່າຍນັກສະແດງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">
              ໃບເບີກຈ່າຍນັກສະແດງ
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຕິດຕາມການຂໍອະນຸມັດ ແລະ ການຈ່າຍຄ່ານັກສະແດງຂອງງານສະແດງ
            </p>
          </div>
          <select
            className="h-9 rounded-md border bg-background px-3 text-sm"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="all">ທຸກສະຖານະ</option>
            <option value="draft">ຮ່າງ</option>
            <option value="pending_approval">ລໍຖ້າອະນຸມັດ</option>
            <option value="approved">ອະນຸມັດແລ້ວ</option>
            <option value="paid">ຈ່າຍແລ້ວ</option>
            <option value="cancelled">ຍົກເລີກ</option>
          </select>
        </div>

        {payoutsQuery.isLoading ? (
          <div className="rounded-xl border bg-card px-5 py-10 text-center text-muted-foreground text-sm">
            ກຳລັງໂຫຼດໃບເບີກຈ່າຍ...
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border bg-card">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <WalletIcon className="size-12 text-muted-foreground" />
                </EmptyMedia>
                <EmptyTitle>ຍັງບໍ່ມີໃບເບີກຈ່າຍ</EmptyTitle>
                <EmptyDescription>
                  {status === "all"
                    ? "ຍັງບໍ່ມີໃບເບີກຈ່າຍນັກສະແດງໃນລະບົບ — ສ້າງຈາກໜ້າລາຍລະອຽດງານສະແດງ"
                    : "ບໍ່ມີໃບເບີກຈ່າຍທີ່ກົງກັບສະຖານະທີ່ເລືອກ"}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-5 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ເລກທີ່
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ງານສະແດງ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ນັກສະແດງ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ສະຖານະ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ຍອດຈ່າຍ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ຈັດການ
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((payout) => (
                  <tr key={payout.id} className="hover:bg-muted/20">
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        className="font-semibold hover:underline"
                        onClick={() =>
                          nav({
                            to: "/app/performance/payouts/$id",
                            params: { id: payout.id },
                          })
                        }
                      >
                        {payout.payoutNo}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">
                        {payout.eventTitle ?? payout.eventId}
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-muted-foreground text-xs">
                        <CalendarIcon className="size-3.5" />
                        {formatDate(payout.eventDate)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <UserIcon className="size-4 text-muted-foreground" />
                        <span>
                          {payout.isBatch
                            ? `${payout.performerCount} ຄົນ`
                            : (payout.performerName ??
                              payout.performerId ??
                              "—")}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={STATUS_VARIANTS[payout.status] ?? "secondary"}
                      >
                        {STATUS_LABELS[payout.status] ?? payout.status}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                      {formatMoney(payout.totalAmount)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() =>
                            nav({
                              to: "/app/performance/payouts/$id",
                              params: { id: payout.id },
                            })
                          }
                        >
                          <EyeIcon className="size-4" /> ລາຍລະອຽດ
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          title="ໄປໜ້າງານສະແດງ"
                          onClick={() =>
                            nav({
                              to: "/app/performance/events/$id",
                              params: { id: payout.eventId },
                            })
                          }
                        >
                          <ExternalLinkIcon className="size-4" />
                        </Button>
                        {(payout.status === "approved" ||
                          payout.status === "paid") && (
                            <PerformanceBrandPrintMenu
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              showLabel={false}
                              buildPrintUrl={(lang) =>
                                performanceApi.payouts.printUrl(payout.id, lang)
                              }
                            />
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Main>
    </>
  );
}
